import { localQueryClient } from '@app/icare/client/localQueryClient'
import { extractTablesFromSql } from '@pkg/api/query/extractTablesFromSql'
import { isDev } from '@pkg/shared/env'
import { logger } from '@pkg/shared/logger'
import type { QueryClient } from '@tanstack/react-query'

/**
 * Table → queryHash index for cache invalidation.
 *
 * Each `localQueryOptions` / `localQueryOneOptions` query carries
 * `meta.tables` (explicit) or `meta.sql` (parsed). When a sync message
 * touches a row, the invalidation hook uses this index to pre-filter
 * candidate queries before running the id-scan.
 *
 * Lifecycle: populated on `added`, dropped on `removed`. The `removed`
 * event fires on garbage-collection (after `gcTime`), not when observer
 * count drops to zero — so a stale-but-harmless entry can linger between
 * observer-zero and GC. Invalidating an unmounted query is a no-op for
 * the user.
 */

interface LocalMeta {
  tables?: readonly string[]
  sql?: string
}

const tablesFromMeta = (meta: LocalMeta | undefined): readonly string[] => {
  if (!meta) return []
  if (meta.tables?.length) return meta.tables
  if (meta.sql) return extractTablesFromSql(meta.sql)
  return []
}

const driftCheck = (declared: readonly string[], sql: string | undefined) => {
  if (!sql) return
  const derived = extractTablesFromSql(sql)
  const missing = derived.filter(t => !declared.includes(t))
  if (missing.length === 0) return
  logger.warn(
    { declared, derived, missing, sql: sql.slice(0, 160) },
    '[queryIndex] declared `tables` does not include tables that the SQL parser surfaced; cache invalidation may under-fire',
  )
}

export interface QueryIndex {
  /** Stop the subscription and clear both maps. */
  dispose: () => void
  /** Read-only view of `table → queryHashes`. */
  getTableIndex: () => ReadonlyMap<string, ReadonlySet<string>>
  /** Collect every queryHash that reads from any of the given tables. */
  tableHashesFor: (tables: Iterable<string>) => Set<string>
}

export function createQueryIndex(client: QueryClient): QueryIndex {
  const tableIndex = new Map<string, Set<string>>()
  const queryTables = new Map<string, Set<string>>()

  const recordTables = (queryHash: string, tables: readonly string[]) => {
    if (!tables.length) return
    if (queryTables.has(queryHash)) return // dedupe re-emits

    const set = new Set<string>(tables)
    queryTables.set(queryHash, set)

    for (const t of set) {
      let hashes = tableIndex.get(t)
      if (!hashes) {
        hashes = new Set()
        tableIndex.set(t, hashes)
      }
      hashes.add(queryHash)
    }
  }

  const dropTables = (queryHash: string) => {
    const set = queryTables.get(queryHash)
    if (!set) return
    queryTables.delete(queryHash)
    for (const t of set) {
      const hashes = tableIndex.get(t)
      if (!hashes) continue
      hashes.delete(queryHash)
      if (hashes.size === 0) tableIndex.delete(t)
    }
  }

  const cache = client.getQueryCache()

  // Capture queries that exist before the subscription is wired up.
  for (const query of cache.getAll()) {
    const meta = query.meta as LocalMeta | undefined
    const tables = tablesFromMeta(meta)
    if (tables.length) recordTables(query.queryHash, tables)
  }

  const unsubscribe = cache.subscribe(event => {
    if (event.type === 'added') {
      const meta = event.query.meta as LocalMeta | undefined
      const tables = tablesFromMeta(meta)
      if (tables.length) recordTables(event.query.queryHash, tables)

      if (isDev && meta?.tables?.length) {
        driftCheck(meta.tables, meta.sql)
      }
      return
    }
    if (event.type === 'removed') {
      dropTables(event.query.queryHash)
      return
    }
  })

  return {
    dispose: () => {
      unsubscribe()
      tableIndex.clear()
      queryTables.clear()
    },
    getTableIndex: () => tableIndex,
    tableHashesFor: tables => {
      const out = new Set<string>()
      for (const t of tables) {
        const hashes = tableIndex.get(t)
        if (!hashes) continue
        for (const h of hashes) out.add(h)
      }
      return out
    },
  }
}

let appIndex: QueryIndex | null = null

export function startQueryIndex(): QueryIndex {
  if (appIndex) return appIndex
  appIndex = createQueryIndex(localQueryClient)
  return appIndex
}

export function disposeQueryIndex() {
  appIndex?.dispose()
  appIndex = null
}

export function tableHashesFor(tables: Iterable<string>): Set<string> {
  if (!appIndex) return new Set()
  return appIndex.tableHashesFor(tables)
}

export function getTableIndex(): ReadonlyMap<string, ReadonlySet<string>> {
  return appIndex?.getTableIndex() ?? new Map()
}
